// docs/58 — Outfitting: what a player does with the World's starting gold while they first join.
// The founding purse (its balance and where the rest is saved), the player's company (each hero with
// its recruit fee and the five stat cards it may train once each), the recruit tile, and the World's
// shared founding market (buy only; a bought piece is replaced by a fresh draw at once). While the
// purse is open each hero can be edited (the forge reopens on their choices) or removed (docs/82). The
// World lobby shows the company and the market as two panels side by side (docs/58 FO10); for a player
// joining a begun World both sit in one panel in a modal on the World screen with the Set out button.
import { gradeTier, gradeWord } from '../../core/sim/gear';
import { ItemIcon, SetFooter } from '../inventory/ItemCard';
import { GearRow } from '../inventory/Inventory';
import { openInventory } from '../inventory/store';
import { openHeroSheet } from '../heroes/heroSheetStore';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, HeroSprite, OryxIcon, Select, confirmDialog } from '../components';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex } from '../../core/net/connection';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import { useSession } from '../../core/stores/session';
import { useFoundingMarket, useFoundingOutfits, useFoundingPurses, useWorldHeroes, useWorldItems, useWorldSettings } from '../../core/subscriptions/hooks';
import { getBridge } from '../../core/encounter/session';
import { catalogueDetails, itemDetails } from '../../core/sim/items';
import type { FoundingMarket, Hero } from '../../bindings/types';
import { PHASE_GATHERING } from './PlayOnline';
import { cap } from './forge';
import { BASICS_SLOT, BOOST_COST, BOOSTS, MAX_HEROES, purseView, recruitState, savedLine, type PurseView, type Recruit } from './founding';
import './outfitting.css';

/** My purse, my company and what one more hero costs, for any screen that forges. */
export function useFounding(worldId: bigint | undefined): { purse: PurseView; mine: Hero[]; recruit: Recruit; gathering: boolean } {
  const me = useSession((s) => s.identity ?? '');
  const settings = useWorldSettings(worldId);
  const purses = useFoundingPurses(worldId);
  const heroes = useWorldHeroes(worldId);
  const gathering = settings?.phase === PHASE_GATHERING;
  const row = purses.find((p) => identityHex(p.owner) === me);
  const purse = purseView(row, settings?.options.startingGold ?? 0, gathering);
  const mine = heroes.filter((h) => identityHex(h.owner) === me && h.status !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
  return { purse, mine, recruit: recruitState(mine.length, purse), gathering };
}

/** The company and the market in one panel: the World screen's arrival modal. */
export function OutfitPanel({ worldId, onRecruit, onEdit, arrival, className = '' }: { worldId: bigint; onRecruit: () => void; onEdit: (h: Hero) => void; arrival?: boolean; className?: string }) {
  return (
    <CompanyPanel worldId={worldId} onRecruit={onRecruit} onEdit={onEdit} arrival={arrival} className={className}>
      <MarketPanel worldId={worldId} embedded />
    </CompanyPanel>
  );
}

/** The founding purse and the player's company: each hero with its edit buttons and, on a second
 *  row, the five stat cards it may train once each; the recruit tile; Set out on arrival. */
export function CompanyPanel({ worldId, onRecruit, onEdit, arrival, className = '', children }: { worldId: bigint; onRecruit: () => void; onEdit: (h: Hero) => void; arrival?: boolean; className?: string; children?: ReactNode }) {
  const push = useToasts((s) => s.push);
  const { purse, mine, recruit, gathering } = useFounding(worldId);
  const outfits = useFoundingOutfits(worldId);
  const [busy, setBusy] = useState<string | undefined>();
  const open = purse.open;
  const run = async (key: string, fn: () => Promise<{ ok: boolean }>) => { setBusy(key); const r = await fn(); setBusy(undefined); return r.ok; };
  const dismiss = async (h: Hero) => {
    const ok = await confirmDialog({ title: `Remove ${h.name} from your company?`, text: 'While you outfit a hero can simply be unmade: their recruit fee and trained cards come back to your purse and their starting kit goes with them, wherever it is. Market goods, anything your other heroes handed them and any gold beyond their background\'s go to your next hero.', confirmLabel: 'Remove', danger: true });
    if (ok) await rpc.dismissHero(h.id);
  };
  const setOut = async () => {
    const ok = await confirmDialog({ title: 'Set out?', text: `${savedLine(purse.left, mine[0]?.name, false)} Founding gold cannot be spent again.`, confirmLabel: 'Set out' });
    if (ok && await run('setout', () => rpc.finishOutfitting(worldId))) push(purse.left > 0 ? `Your company sets out with ${purse.left} gold saved.` : 'Your company sets out.', 'success');
  };

  return (
    <section className={`fk-lobby__panel fk-outfit ${className}`} aria-label="Outfit your company" data-testid="outfit" data-open={open} data-left={purse.left}>
      <div className="fk-lobby__panel-head">
        <h2 className="fk-lobby__panel-title">Your company <span className="fk-lobby__count">{mine.length} / {MAX_HEROES}</span></h2>
        <span className="fk-outfit__purse" data-testid="outfit-left" data-left={purse.left} title="Founding gold: spent only while you first join this World">
          <OryxIcon name="item_coin_bag" scale={1} /><b>{open ? purse.left : purse.saved}</b>{open ? <> of {purse.total} founding gold</> : <> gold saved</>}
        </span>
      </div>
      <p className="fk-outfit__lede">{open
        ? <>First hero free; recruit, train or shop only now, and open a hero's gear to wear what you buy. {savedLine(purse.left, mine[0]?.name, gathering)}</>
        : <>Your company has set out; founding gold is spent. More heroes join by hiring in the villages.</>}</p>

      <ul className="fk-outfit__company" aria-label="Your heroes" data-testid="my-lobby-heroes">
        {mine.map((h) => {
          const o = outfits.find((x) => x.heroId === h.id);
          const cls = CLASS_NAMES[h.class] ?? 'fighter';
          const trained = new Set(o?.boosts ?? []);
          return (
            <li key={h.id.toString()} className="fk-outfit__hero" data-testid="lobby-hero" data-hero-name={h.name} title={`${h.name} · ${cap(SPECIES_NAMES[h.species] ?? '')} ${cap(cls)}`}>
              <button type="button" className="fk-hero-open" onClick={() => openHeroSheet(h.id)} aria-label={`${h.name}: see their stats card`} title={`${h.name}'s stats card`} data-testid="lobby-hero-sheet">
                <HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={cls} scale={2} title={h.name} />
              </button>
              <span className="fk-outfit__who">
                <b className="fk-outfit__name"><span>{h.name}</span>{o && o.recruitFee > 0 && <Badge tone="grey" title="Recruited from the founding purse">{o.recruitFee}g</Badge>}</b>
                <span className="fk-outfit__sub">{cap(SPECIES_NAMES[h.species] ?? '')} {cap(cls)} · Lv {h.level} · <span className="fk-lobby__tile-gold">{h.gold} gold</span></span>
              </span>
              {open && (
                <span className="fk-outfit__edit" role="group" aria-label={`Change ${h.name}`}>
                  <Button small disabled={!!busy || h.status !== 0} onClick={() => onEdit(h)} title={h.status !== 0 ? `${h.name} is in the field` : `Change ${h.name}'s name, class, species, background, loadout or calling`} data-testid="lobby-hero-edit">Edit</Button>
                  <Button small variant="ghost" disabled={!!busy || h.status === 1} onClick={() => void dismiss(h)} title={`Remove ${h.name} from your company`} data-testid="lobby-hero-dismiss">Remove</Button>
                </span>
              )}
              <GearRow worldId={worldId} heroId={h.id} name={h.name} />
              <span className="fk-outfit__boosts" role="group" aria-label={`Train ${h.name}`}>
                {BOOSTS.map((b) => {
                  const on = trained.has(b.card);
                  const key = `boost-${h.id}-${b.card}`;
                  const can = open && (on || purse.left >= BOOST_COST);
                  return (
                    <button key={b.card} type="button" className={`fk-outfit__boost ${on ? 'fk-outfit__boost--on' : ''}`} aria-pressed={on} disabled={!can || !!busy}
                      title={on ? `${b.name}: ${b.effect} — press to return it (+${BOOST_COST}g)` : open ? `Train ${b.name}: ${b.effect} for ${BOOST_COST} gold` : `${b.name}: ${b.effect}`}
                      onClick={() => void run(key, () => (on ? rpc.returnBoost(h.id, b.card) : rpc.buyBoost(h.id, b.card)))} data-testid="outfit-boost" data-card={b.card} data-on={on}>
                      <span className="fk-outfit__boost-name">{b.name}</span><span className="fk-outfit__boost-fx">{b.short}</span>
                    </button>
                  );
                })}
              </span>
            </li>
          );
        })}
        {mine.length < MAX_HEROES && (open || mine.length === 0) && (
          <li className="fk-outfit__hero fk-outfit__hero--new">
            <button type="button" className="fk-outfit__recruit" disabled={!recruit.ok} onClick={onRecruit} data-testid="lobby-forge-open" data-first={mine.length === 0} data-fee={recruit.fee}
              title={recruit.why ?? (recruit.fee ? `Recruit another hero for ${recruit.fee} founding gold` : 'Forge your first hero')}>
              <span className="fk-outfit__recruit-plus" aria-hidden="true">+</span>
              <span>{mine.length === 0 ? 'Forge your first hero' : 'Recruit a hero'}<small>{recruit.fee ? `${recruit.fee} gold` : 'free'}</small></span>
            </button>
            {recruit.why && mine.length > 0 && <span className="fk-outfit__why">{recruit.why}</span>}
          </li>
        )}
      </ul>

      {children}

      {arrival && open && (
        <div className="fk-outfit__go">
          <Button primary size="lg" disabled={mine.length === 0 || !!busy} loading={busy === 'setout'} onClick={() => void setOut()} data-testid="outfit-set-out"
            title={mine.length === 0 ? 'Forge a hero first' : undefined}>Set out{purse.left > 0 ? ` · save ${purse.left} gold` : ''}</Button>
        </div>
      )}
    </section>
  );
}

/** The World's shared founding market (FO4): four pieces a player, each one a founding purse can
 *  pay for (FO9). Its own panel in the World lobby; `embedded` inside the company panel. */
export function MarketPanel({ worldId, embedded, className = '' }: { worldId: bigint; embedded?: boolean; className?: string }) {
  const push = useToasts((s) => s.push);
  const { purse, mine } = useFounding(worldId);
  const stall = [...useFoundingMarket(worldId)].sort((a, b) => a.slot - b.slot);
  // docs/159 SB2: the basics shelf (slots from BASICS_SLOT) never sells out; the rest is the drawn stall
  const basics = stall.filter((m) => m.slot >= BASICS_SLOT);
  const market = stall.filter((m) => m.slot < BASICS_SLOT);
  const [payerId, setPayerId] = useState<bigint | undefined>();
  const [busy, setBusy] = useState<string | undefined>();
  // the piece open in its popup, and the button it opened from (the popup sits by it)
  const [peek, setPeek] = useState<{ id: bigint; anchor: HTMLElement } | undefined>();
  // docs/146: the last piece bought, with a way straight to it in its hero's inventory
  const [bought, setBought] = useState<{ heroId: bigint; heroName: string; def: number; name: string } | undefined>();
  const items = useWorldItems(worldId);
  const payer = mine.find((h) => h.id === payerId) ?? mine[0];
  const peekPiece = stall.find((m) => m.id === peek?.id);
  const open = purse.open;
  const whyNot = (m: FoundingMarket) => (!open ? 'Only while you first outfit' : !payer ? 'Forge a hero to carry it first' : purse.left < m.price ? `You have ${purse.left} founding gold` : undefined);
  const buy = async (m: FoundingMarket) => {
    if (!payer) return;
    setBusy(`market-${m.id}`);
    const r = await rpc.buyMarketPiece(m.id, payer.id);
    setBusy(undefined);
    if (r.ok) { push(`${payer.name} takes the ${m.name.toLowerCase()}.`, 'success'); setBought({ heroId: payer.id, heroName: payer.name, def: m.def, name: m.name }); }
  };
  const openBought = () => {
    if (!bought) return;
    // the newest piece of that kind on that hero is the one just bought
    const it = items.filter((i) => i.heroId === bought.heroId && i.def === bought.def).sort((a, b) => (a.id < b.id ? 1 : -1))[0];
    openInventory(bought.heroId, it ? { select: it.id } : undefined);
  };
  const Title = embedded ? 'h3' : 'h2';
  const good = (m: FoundingMarket) => {
    const why = whyNot(m);
    const on = m.id === peekPiece?.id;
    const basic = m.slot >= BASICS_SLOT;
    return (
      <li key={m.id.toString()} className={`fk-outfit__good ${on ? 'fk-outfit__good--on' : ''}${basic ? ' fk-outfit__good--basic' : ''}`} data-testid={basic ? 'market-basic' : 'market-piece'} data-name={m.name} data-def={m.def} data-price={m.price}>
        <button type="button" className="fk-outfit__good-peek" aria-expanded={on} onClick={(e) => setPeek(on ? undefined : { id: m.id, anchor: e.currentTarget })} title={`What the ${m.name.toLowerCase()} does`} data-testid="market-peek">
          <ItemIcon def={m.def} tier={m.tier} name={m.name} scale={basic ? 1 : 2} framed />
          <span className="fk-outfit__good-name">{m.name}{(!basic || gradeTier(m.def, m.tier) > 1) && <Badge tone={tierTone(gradeTier(m.def, m.tier))}>{gradeWord(m.def, m.tier)}</Badge>}</span>
        </button>
        <Button small primary={!why} disabled={!!why || !!busy} loading={busy === `market-${m.id}`} title={why ?? `Buy for ${payer?.name}`} onClick={() => void buy(m)} data-testid={basic ? 'market-basic-buy' : 'market-buy'}>{m.price}g</Button>
      </li>
    );
  };

  return (
    <div className={`${embedded ? 'fk-outfit__market--embedded' : 'fk-lobby__panel'} fk-outfit__market ${className}`} role="region" aria-label="Founding market" data-testid="market" data-pieces={market.length}>
      <div className="fk-outfit__market-head">
        <Title className="fk-lobby__panel-title">Founding market{!embedded && <span className="fk-lobby__count">{market.length}</span>}</Title>
        {!embedded && <span className="fk-outfit__purse" title="Your founding gold"><OryxIcon name="item_coin_bag" scale={1} /><b>{open ? purse.left : 0}</b> to spend</span>}
        <span className="fk-outfit__market-note">Shared by everyone joining: four pieces a player, each within the founding gold. Buy only — a bought piece is gone, and a fresh one takes its place. The basics never run out.</span>
        {mine.length > 1 && open && (
          <label className="fk-outfit__payer"><span>For</span>
            <Select value={payer?.id.toString() ?? ''} onChange={(e) => setPayerId(BigInt(e.target.value))} data-testid="market-payer">
              {mine.map((h) => <option key={h.id.toString()} value={h.id.toString()}>{h.name}</option>)}
            </Select>
          </label>
        )}
      </div>
      {market.length === 0 && <p className="fk-outfit__market-empty fk-faint" data-testid="market-empty">Nothing on the stall fits a purse of {purse.total} gold.</p>}
      <ul className="fk-outfit__goods">{market.map(good)}</ul>
      {basics.length > 0 && (
        <section className="fk-outfit__basics" aria-label="The basics" data-testid="market-basics" data-pieces={basics.length}>
          <h4 className="fk-outfit__basics-title">The basics <span className="fk-faint">always in stock</span></h4>
          <ul className="fk-outfit__goods fk-outfit__goods--basics">{basics.map(good)}</ul>
        </section>
      )}
      {bought && mine.some((h) => h.id === bought.heroId) && (
        <p className="fk-outfit__bought" role="status" data-testid="market-bought">
          <span>{bought.heroName} carries the {bought.name.toLowerCase()} in their pack.</span>
          <Button small variant="primary" onClick={openBought} data-testid="market-bought-open" icon={<OryxIcon name="item_147" scale={1} />}>Open pack</Button>
          <Button small variant="ghost" onClick={() => setBought(undefined)} aria-label="Dismiss">✕</Button>
        </p>
      )}
      {peek && peekPiece && <MarketDetails anchor={peek.anchor} piece={peekPiece} why={whyNot(peekPiece)} payer={payer?.name} busy={busy} onBuy={() => void buy(peekPiece)} onClose={() => setPeek(undefined)} />}
    </div>
  );
}

const tierTone = (tier: number) => (tier >= 3 ? 'gold' : tier === 2 ? 'green' : 'grey');

const POP_GAP = 8;
const POP_EDGE = 8;

/** A market piece opened up in a popup by its button: where it goes, who may use it and what it adds,
 *  from the catalogue. It sits under its button (over it when there is no room below), closes on
 *  Escape, on a press outside it, or with its own Close. */
function MarketDetails({ anchor, piece, why, payer, busy, onBuy, onClose }: { anchor: HTMLElement; piece: FoundingMarket; why?: string; payer?: string; busy?: string; onBuy: () => void; onClose: () => void }) {
  const card = getBridge()?.itemCard(piece.def, piece.tier);
  const d = catalogueDetails(piece.def) ?? (card && itemDetails(card));
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number }>();
  useLayoutEffect(() => {
    const place = () => {
      if (!ref.current) return;
      const a = anchor.getBoundingClientRect(), t = ref.current.getBoundingClientRect();
      const below = a.bottom + POP_GAP + t.height <= window.innerHeight - POP_EDGE;
      const top = below ? a.bottom + POP_GAP : Math.max(POP_EDGE, a.top - POP_GAP - t.height);
      const left = Math.max(POP_EDGE, Math.min(window.innerWidth - t.width - POP_EDGE, a.left));
      setAt({ left, top });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [anchor, piece.id]);
  useEffect(() => {
    const down = (e: PointerEvent) => { const t = e.target as Node; if (!ref.current?.contains(t) && !anchor.contains(t)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down); document.removeEventListener('keydown', key); };
  }, [anchor, onClose]);
  return createPortal(
    <div ref={ref} className="fk-outfit__pop" style={at ? { left: at.left, top: at.top } : { left: -9999, top: -9999, visibility: 'hidden' }}>
    <div className="fk-outfit__peek" role="dialog" aria-label={`${piece.name} details`} data-testid="market-details" data-name={piece.name}>
      <ItemIcon def={piece.def} tier={piece.tier} name={piece.name} scale={3} framed />
      <div className="fk-outfit__peek-body">
        <b className="fk-outfit__peek-name">{piece.name}<Badge tone={tierTone(gradeTier(piece.def, piece.tier))}>{gradeWord(piece.def, piece.tier)}</Badge></b>
        {d ? (
          <>
            <span className="fk-outfit__peek-kind">{d.kind} · {d.classes}</span>
            <ul className="fk-outfit__peek-fx">
              {d.effects.length ? d.effects.map((e) => <li key={e}>{e}</li>) : <li className="fk-faint">No bonuses of its own.</li>}
            </ul>
          </>
        ) : <span className="fk-outfit__peek-kind">Details are still loading.</span>}
        <SetFooter def={piece.def} compact />
      </div>
      <div className="fk-outfit__peek-actions">
        <Button small primary={!why} disabled={!!why || !!busy} loading={busy === `market-${piece.id}`} title={why ?? `Buy for ${payer}`} onClick={onBuy} data-testid="market-details-buy">Buy · {piece.price}g</Button>
        <Button small variant="ghost" onClick={onClose} data-testid="market-details-close">Close</Button>
      </div>
    </div>
    </div>,
    document.body,
  );
}
